import { useState, useMemo } from 'react';

export type SortDir = 'asc' | 'desc';

export interface SortState<K extends string> {
  key: K | null;
  dir: SortDir;
}

/** Pure toggle rule: 1st click asc, 2nd desc, 3rd resets to unsorted. */
function nextSort<K extends string>(prev: SortState<K>, key: K): SortState<K> {
  if (prev.key === key) {
    return prev.dir === 'asc' ? { key, dir: 'desc' } : { key: null, dir: 'asc' };
  }
  return { key, dir: 'asc' };
}

export function useSortable<T, K extends string>(
  data: T[],
  accessor: (item: T, key: K) => string | number | null,
  /** Controlled mode: when provided, sort state is owned by the parent (so it can
   *  be SHARED across sibling tables / persisted). Omit to keep local state. The
   *  controlled-vs-uncontrolled choice must be fixed per call site (don't flip). */
  controlled?: { sort: SortState<K>; onSortChange: (next: SortState<K>) => void },
) {
  // Uncontrolled fallback (used only when `controlled` is absent).
  const [internal, setInternal] = useState<SortState<K>>({ key: null, dir: 'asc' });
  const sort = controlled ? controlled.sort : internal;

  const toggle = (key: K) => {
    const next = nextSort(sort, key);
    if (controlled) controlled.onSortChange(next);
    else setInternal(next);
  };

  const sorted = useMemo(() => {
    if (!sort.key) return data;
    const k = sort.key;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => {
      const va = accessor(a, k);
      const vb = accessor(b, k);
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      if (typeof va === 'string' && typeof vb === 'string') {
        return va.localeCompare(vb) * dir;
      }
      return ((va as number) - (vb as number)) * dir;
    });
  }, [data, sort, accessor]);

  return { sorted, sort, toggle };
}
